import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createPersistedMockData } from '../mockDataManager';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import {
  listEmailTemplates as listEmailTemplatesGobodo,
  getEmailTemplateById as getEmailTemplateByIdGobodo,
  createEmailTemplate as createEmailTemplateGobodo,
  updateEmailTemplate as updateEmailTemplateGobodo,
  deleteEmailTemplate as deleteEmailTemplateGobodo,
} from '../gobodo/gobodoEmailTemplateService';
import type { EmailTemplate as FullEmailTemplate } from '../../types/emailTemplates';

// ============================================
// TYPES
// ============================================

export type TemplateCategory = 'user' | 'client' | 'case' | 'notification' | 'system' | 'custom';

export interface MergeField {
  id: string;
  label: string;
  value: string; // e.g., {{user.firstName}}
  category: string;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string; // HTML content
  category: TemplateCategory;
  description?: string;
  isActive: boolean;
  isSystem: boolean; // Cannot be deleted
  mergeFields: string[]; // List of merge field IDs used in template
  lastUsed?: string;
  usageCount: number;
  createdBy: string;
  createdDate: string;
  lastModified: string;
  version: number;
}

export interface TemplateVersion {
  id: string;
  templateId: string;
  version: number;
  subject: string;
  body: string;
  createdBy: string;
  createdDate: string;
}

// ============================================
// MOCK DATA
// ============================================

export const availableMergeFields: MergeField[] = [
  // User fields
  { id: 'user.firstName', label: 'User First Name', value: '{{user.firstName}}', category: 'User' },
  { id: 'user.lastName', label: 'User Last Name', value: '{{user.lastName}}', category: 'User' },
  { id: 'user.email', label: 'User Email', value: '{{user.email}}', category: 'User' },
  { id: 'user.phone', label: 'User Phone', value: '{{user.phone}}', category: 'User' },

  // Client fields
  { id: 'client.name', label: 'Client Name', value: '{{client.name}}', category: 'Client' },
  { id: 'client.email', label: 'Client Email', value: '{{client.email}}', category: 'Client' },
  { id: 'client.phone', label: 'Client Phone', value: '{{client.phone}}', category: 'Client' },

  // Case fields
  { id: 'case.number', label: 'Case Number', value: '{{case.number}}', category: 'Case' },
  { id: 'case.subject', label: 'Case Subject', value: '{{case.subject}}', category: 'Case' },
  { id: 'case.status', label: 'Case Status', value: '{{case.status}}', category: 'Case' },

  // System fields
  { id: 'system.date', label: 'Current Date', value: '{{system.date}}', category: 'System' },
  { id: 'system.time', label: 'Current Time', value: '{{system.time}}', category: 'System' },
  { id: 'system.url', label: 'System URL', value: '{{system.url}}', category: 'System' },
  { id: 'system.companyName', label: 'Company Name', value: '{{system.companyName}}', category: 'System' },
];

const defaultMockTemplates: EmailTemplate[] = [
  {
    id: 'template_001',
    name: 'Welcome Email',
    subject: 'Welcome to {{system.companyName}}, {{user.firstName}}!',
    body: `<h1>Welcome to Product Hub!</h1>
<p>Hi {{user.firstName}},</p>
<p>We're excited to have you on board. Your account has been created successfully.</p>
<p><strong>Your Details:</strong></p>
<ul>
  <li>Email: {{user.email}}</li>
  <li>Phone: {{user.phone}}</li>
</ul>
<p>If you have any questions, feel free to reach out to our support team.</p>
<p>Best regards,<br>The Product Hub Team</p>`,
    category: 'user',
    description: 'Welcome email sent to new users',
    isActive: true,
    isSystem: true,
    mergeFields: ['user.firstName', 'user.email', 'user.phone', 'system.companyName'],
    lastUsed: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    usageCount: 145,
    createdBy: 'System',
    createdDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    version: 2
  },
  {
    id: 'template_002',
    name: 'Password Reset',
    subject: 'Reset Your Password',
    body: `<h2>Password Reset Request</h2>
<p>Hello {{user.firstName}},</p>
<p>We received a request to reset your password. Click the link below to create a new password:</p>
<p><a href="{{system.url}}/reset-password" style="background: #6B46C1; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">Reset Password</a></p>
<p>If you didn't request this, please ignore this email.</p>
<p>This link will expire in 24 hours.</p>`,
    category: 'user',
    description: 'Password reset email',
    isActive: true,
    isSystem: true,
    mergeFields: ['user.firstName', 'system.url'],
    lastUsed: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    usageCount: 89,
    createdBy: 'System',
    createdDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString(),
    version: 3
  },
  {
    id: 'template_003',
    name: 'Client Onboarding',
    subject: 'Welcome to {{system.companyName}} - Let\'s Get Started!',
    body: `<h1>Welcome, {{client.name}}!</h1>
<p>Thank you for choosing Product Hub for your business needs.</p>
<p><strong>Your Account Information:</strong></p>
<ul>
  <li>Client Name: {{client.name}}</li>
  <li>Email: {{client.email}}</li>
  <li>Phone: {{client.phone}}</li>
</ul>
<p>Our team will reach out within 24 hours to complete your setup.</p>
<p>Welcome aboard!</p>`,
    category: 'client',
    description: 'Onboarding email for new clients',
    isActive: true,
    isSystem: false,
    mergeFields: ['client.name', 'client.email', 'client.phone', 'system.companyName'],
    lastUsed: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    usageCount: 34,
    createdBy: 'Admin',
    createdDate: new Date(Date.now() - 200 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    version: 1
  },
  {
    id: 'template_004',
    name: 'Case Update Notification',
    subject: 'Case {{case.number}} Update: {{case.status}}',
    body: `<h2>Case Update</h2>
<p>Hello {{user.firstName}},</p>
<p>Your case has been updated:</p>
<p><strong>Case Details:</strong></p>
<ul>
  <li>Case Number: {{case.number}}</li>
  <li>Subject: {{case.subject}}</li>
  <li>Status: {{case.status}}</li>
</ul>
<p>View your case details in the portal: <a href="{{system.url}}/cases/{{case.number}}">View Case</a></p>`,
    category: 'case',
    description: 'Notification when case is updated',
    isActive: true,
    isSystem: false,
    mergeFields: ['user.firstName', 'case.number', 'case.subject', 'case.status', 'system.url'],
    lastUsed: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    usageCount: 278,
    createdBy: 'Admin',
    createdDate: new Date(Date.now() - 150 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    version: 4
  },
  {
    id: 'template_005',
    name: 'Monthly Newsletter',
    subject: 'Product Hub Newsletter - {{system.date}}',
    body: `<h1>Monthly Update</h1>
<p>Hi {{user.firstName}},</p>
<p>Here's what's new this month at Product Hub:</p>
<h3>New Features</h3>
<ul>
  <li>Enhanced reporting dashboard</li>
  <li>Mobile app improvements</li>
  <li>New integrations available</li>
</ul>
<p>Stay tuned for more updates!</p>`,
    category: 'notification',
    description: 'Monthly newsletter to all users',
    isActive: false,
    isSystem: false,
    mergeFields: ['user.firstName', 'system.date'],
    usageCount: 12,
    createdBy: 'Marketing',
    createdDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    version: 1
  }
];

// Create persisted email templates using centralized mock data manager
const persistedTemplates = createPersistedMockData('crm_email_templates', defaultMockTemplates);

// Export for backwards compatibility
export const mockTemplates = persistedTemplates.data;

/** Map Gobodo (full) EmailTemplate to query-layer EmailTemplate shape */
function mapGobodoToQueryTemplate(full: FullEmailTemplate): EmailTemplate {
  const category = ((full as unknown as Record<string, unknown>).category as TemplateCategory) || 'custom';
  return {
    id: full.id,
    name: full.name,
    subject: full.subject,
    body: full.bodyHtml,
    category,
    description: full.description,
    isActive: full.status === 'active',
    isSystem: full.isSystemTemplate,
    mergeFields: extractMergeFields(full.bodyHtml),
    lastUsed: full.lastUsedAt,
    usageCount: full.usageCount,
    createdBy: full.createdBy,
    createdDate: full.createdAt,
    lastModified: full.updatedAt,
    version: full.versionNumber,
  };
}

// ============================================
// QUERY KEYS
// ============================================

export const emailTemplateKeys = {
  all: ['email-templates'] as const,
  lists: () => [...emailTemplateKeys.all, 'list'] as const,
  list: (filters: string) => [...emailTemplateKeys.lists(), filters] as const,
  details: () => [...emailTemplateKeys.all, 'detail'] as const,
  detail: (id: string) => [...emailTemplateKeys.details(), id] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Get all email templates with optional filtering
 * Future: Replace with API call to GET /api/email-templates
 */
export type EmailTemplateDepartment = 'all' | 'credit_management' | 'cybersweep' | 'credit_block';

export function useEmailTemplates(filters?: {
  searchQuery?: string;
  category?: TemplateCategory | 'all';
  isActive?: boolean;
  department?: EmailTemplateDepartment;
}) {
  return useQuery({
    queryKey: emailTemplateKeys.list(JSON.stringify(filters || {})),
    queryFn: async () => {
      if (shouldUseGobodo()) {
        const list = await listEmailTemplatesGobodo({ onlyCurrentVersions: true });
        let filtered = list;
        if (filters?.department && filters.department !== 'all') {
          filtered = filtered.filter((t) => (t as { department?: string }).department === filters.department);
        }
        let templates = filtered.map(mapGobodoToQueryTemplate);
        if (filters?.searchQuery) {
          const query = filters.searchQuery.toLowerCase();
          templates = templates.filter(
            t =>
              t.name.toLowerCase().includes(query) ||
              t.subject.toLowerCase().includes(query) ||
              t.description?.toLowerCase().includes(query)
          );
        }
        if (filters?.category && filters.category !== 'all') {
          templates = templates.filter(t => t.category === filters.category);
        }
        if (filters?.isActive !== undefined) {
          templates = templates.filter(t => t.isActive === filters.isActive);
        }
        return templates.sort((a, b) => a.name.localeCompare(b.name));
      }

      await new Promise(resolve => setTimeout(resolve, 400));
      let templates = persistedTemplates.get();
      if (filters?.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        templates = templates.filter(
          t =>
            t.name.toLowerCase().includes(query) ||
            t.subject.toLowerCase().includes(query) ||
            t.description?.toLowerCase().includes(query)
        );
      }
      if (filters?.category && filters.category !== 'all') {
        templates = templates.filter(t => t.category === filters.category);
      }
      if (filters?.isActive !== undefined) {
        templates = templates.filter(t => t.isActive === filters.isActive);
      }
      if (filters?.department && filters.department !== 'all') {
        templates = templates.filter(
          t => (t as { department?: string }).department === filters.department || !(t as { department?: string }).department
        );
      }
      return templates.sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Get single email template
 * Future: Replace with API call to GET /api/email-templates/:id
 */
export function useEmailTemplate(templateId: string) {
  return useQuery({
    queryKey: emailTemplateKeys.detail(templateId),
    queryFn: async () => {
      if (shouldUseGobodo()) {
        const full = await getEmailTemplateByIdGobodo(templateId);
        if (!full) throw new Error('Template not found');
        return mapGobodoToQueryTemplate(full as FullEmailTemplate);
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const template = persistedTemplates.get().find(t => t.id === templateId);
      if (!template) throw new Error('Template not found');
      return template;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!templateId,
  });
}

/**
 * Create new email template
 * Future: Replace with API call to POST /api/email-templates
 */
export function useCreateEmailTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateData: Omit<EmailTemplate, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'version' | 'usageCount' | 'lastUsed'>) => {
      if (shouldUseGobodo()) {
        const userId = (templateData as { createdBy?: string }).createdBy || 'Current User';
        const created = await createEmailTemplateGobodo(
          {
            name: templateData.name,
            subject: templateData.subject,
            bodyHtml: templateData.body,
            bodyText: undefined,
            department: 'all',
            variables: [],
            defaultValues: {},
            description: templateData.description,
            status: templateData.isActive ? 'active' : 'draft',
            category: templateData.category,
          },
          userId
        );
        return mapGobodoToQueryTemplate(created as FullEmailTemplate);
      }
      await new Promise(resolve => setTimeout(resolve, 600));
      const newTemplate: EmailTemplate = {
        ...templateData,
        id: `template_${Date.now()}`,
        version: 1,
        usageCount: 0,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
      };
      return persistedTemplates.add(newTemplate);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Update email template
 * Future: Replace with API call to PATCH /api/email-templates/:id
 */
export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, updates }: { templateId: string; updates: Partial<EmailTemplate> }) => {
      if (shouldUseGobodo()) {
        const userId = (updates as { createdBy?: string }).createdBy || 'Current User';
        const payload = {
          name: updates.name,
          subject: updates.subject,
          bodyHtml: updates.body,
          bodyText: undefined,
          status: updates.isActive === false ? 'inactive' : updates.isActive ? 'active' : undefined,
          variables: [],
          defaultValues: {},
          category: updates.category,
          createNewVersion: (updates as { createNewVersion?: boolean }).createNewVersion,
        };
        const full = await updateEmailTemplateGobodo(templateId, payload as import('../../types/emailTemplates').UpdateTemplateRequest & { category?: string }, userId);
        return mapGobodoToQueryTemplate(full as FullEmailTemplate);
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      const currentTemplate = persistedTemplates.get().find(t => t.id === templateId);
      if (!currentTemplate) throw new Error('Template not found');
      if (currentTemplate.isSystem && updates.isActive === false) {
        throw new Error('Cannot deactivate system templates');
      }
      const finalUpdates = {
        ...updates,
        version: currentTemplate.version + 1,
        lastModified: new Date().toISOString(),
      };
      const updated = persistedTemplates.update(templateId, finalUpdates);
      if (!updated) throw new Error('Template not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Delete email template
 * Future: Replace with API call to DELETE /api/email-templates/:id
 */
export function useDeleteEmailTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      if (shouldUseGobodo()) {
        const full = await getEmailTemplateByIdGobodo(templateId);
        if (!full) throw new Error('Template not found');
        if (full.isSystemTemplate) throw new Error('Cannot delete system templates');
        await deleteEmailTemplateGobodo(templateId);
        return { templateId };
      }
      await new Promise(resolve => setTimeout(resolve, 400));
      const template = persistedTemplates.get().find(t => t.id === templateId);
      if (!template) throw new Error('Template not found');
      if (template.isSystem) throw new Error('Cannot delete system templates');
      const success = persistedTemplates.remove(templateId);
      if (!success) throw new Error('Template not found');
      return { templateId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Clone email template
 * Future: Replace with API call to POST /api/email-templates/:id/clone
 */
export function useCloneEmailTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      if (shouldUseGobodo()) {
        const full = await getEmailTemplateByIdGobodo(templateId);
        if (!full) throw new Error('Template not found');
        const created = await createEmailTemplateGobodo(
          {
            name: `${full.name} (Copy)`,
            subject: full.subject,
            bodyHtml: full.bodyHtml,
            bodyText: full.bodyText,
            department: full.department,
            variables: full.variables,
            defaultValues: full.defaultValues,
            description: full.description,
            status: 'draft',
            category: (full as unknown as Record<string, unknown>).category as string,
          },
          full.createdBy
        );
        return mapGobodoToQueryTemplate(created as FullEmailTemplate);
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      const template = persistedTemplates.get().find(t => t.id === templateId);
      if (!template) throw new Error('Template not found');
      const clonedTemplate: EmailTemplate = {
        ...template,
        id: `template_${Date.now()}`,
        name: `${template.name} (Copy)`,
        isSystem: false,
        version: 1,
        usageCount: 0,
        lastUsed: undefined,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
      };
      return persistedTemplates.add(clonedTemplate);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.lists() });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get category label
 */
export function getCategoryLabel(category: TemplateCategory): string {
  const labels: Record<TemplateCategory, string> = {
    user: 'User',
    client: 'Client',
    case: 'Case',
    notification: 'Notification',
    system: 'System',
    custom: 'Custom'
  };
  return labels[category];
}

/**
 * Get category color
 */
export function getCategoryColor(category: TemplateCategory): string {
  const colors: Record<TemplateCategory, string> = {
    user: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
    client: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
    case: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400',
    notification: 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400',
    system: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    custom: 'bg-pink-100 text-pink-700 dark:bg-pink-900/20 dark:text-pink-400'
  };
  return colors[category];
}

/**
 * Extract merge fields from template body
 */
export function extractMergeFields(body: string): string[] {
  const regex = /\{\{([^}]+)\}\}/g;
  const matches = Array.from(body.matchAll(regex));
  const fields = new Set<string>();

  for (const match of matches) {
    fields.add(match[1].trim());
  }

  return Array.from(fields);
}

/**
 * Replace merge fields with sample data
 */
export function replaceMergeFields(text: string): string {
  const sampleData: Record<string, string> = {
    'user.firstName': 'John',
    'user.lastName': 'Doe',
    'user.email': 'john.doe@example.com',
    'user.phone': '+1 (555) 123-4567',
    'client.name': 'Acme Corporation',
    'client.email': 'contact@acme.com',
    'client.phone': '+1 (555) 987-6543',
    'case.number': 'CASE-00123',
    'case.subject': 'Technical Support Request',
    'case.status': 'In Progress',
    'system.date': new Date().toLocaleDateString(),
    'system.time': new Date().toLocaleTimeString(),
    'system.url': 'https://app.producthub.com',
    'system.companyName': 'Product Hub'
  };

  let result = text;
  for (const [key, value] of Object.entries(sampleData)) {
    const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
    result = result.replace(regex, value);
  }

  return result;
}
